/* DRIVE IQ — ADVENTURE MODE V600.13 FAMILY DISCOVERY QUERY EXPANSION
   File: adventure-controls.js

   V600.13 focused repair:
   - Expands only the Family IQ discovery query so family scans can surface a broader real-world attraction mix.
   - Adds family-friendly attractions, aquariums, water parks, amusement, arcades, mini golf, playgrounds, zoos, museums, and dolphin cruises.
   - Leaves category rescan authority, scope, scan controller, provider, ranking, photos, pins, and routing unchanged.

   V600.12 focused repair:
   - Category chip taps now immediately launch a replacement scan through the existing Scan IQ authority.
   - Preserves the current scope and destination/route/current-location center.
   - Forces a fresh market pass for the selected category without touching provider, ranking, photo, pin, or routing ownership.

   V600.11 focused repair:
   - Coordinates the final post-scan experience with Scan IQ V600.78.
   - Treats scan:presentation-ready as the only authority for closing the full
     Adventure menu after a successful initial scan.
   - Minimizes the menu first, then verifies the compact Top Picks shelf, Places
     pill, pins, and map interaction after layout settlement.
   - Preserves the automatic shelf opened by Scan IQ and never hides or replaces it.
   - Adds one guarded shelf-open fallback when the presentation event reports that
     the shelf did not mount successfully.
   - Uses the immutable committed result count supplied by Scan IQ instead of an
     early provider, toast, or recommendation-batch count.
   - Keeps the Places pill and shelf interactive while all invisible menu overlays
     surrender touch authority.
   - Prevents duplicate presentation events from replaying animations or reopening
     a shelf the user has already closed.


   V600.10 focused repair:
   - Makes the minimized Adventure menu fully surrender touch authority to the
     Mapbox canvas, map pins, Places pill, and results shelf.
   - Detects and disables invisible full-screen Adventure overlays that remain
     mounted after the panel visually closes.
   - Restores those overlay pointer-event values when the Adventure menu reopens.
   - Keeps only the compact menu-restore controls interactive while minimized.
   - Stops Controls from independently opening the results shelf or forcing the
     Places pill during scan completion.
   - Waits for Scan IQ's scan:presentation-ready event before minimizing the menu
     and releasing map interaction.
   - Repeats touch-authority recovery after layout transitions at 0, 100, and
     300 milliseconds so delayed CSS callbacks cannot rebuild an invisible shield.
   - Adds runtime diagnostics for the element currently receiving map-area taps.


   Purpose:
   - Connect the existing Adventure Custom Liquid controls to V600.
   - Own button, search, category, scope, GPS, center-map, and menu bindings.
   - Never creates Mapbox, pins, Place IQ, scan results, or route payloads.
   - Uses the V600 controllers exclusively.
   - Preserves the selected destination name in the search field.
   - Commits the exact selected destination once.
   - Automatically starts a near-destination scan after dropdown selection.
   - Prevents duplicate selection callbacks from launching duplicate scans.
   - Turns the bottom CTA into SEARCH ANOTHER PLACE after results exist.
   - Reopens the Adventure panel without leaving or reloading Adventure Mode.
   - Closes Place IQ and the results shelf before starting a new search.
   - Clears the previous destination and focuses the destination field.
   - Converts Search IQ natural intent into the correct category, scope, center, and query.
   - Starts Near Me intents from current GPS without showing destination autocomplete.
   - Preserves category intent while explicit cities and districts resolve through destination search.
   - Supports future Along Route intent without changing scan-provider ownership.
   - Prevents duplicate intent and destination events from launching duplicate scans.
   - Forces explicit Near Me intent to obtain a fresh GPS fix instead of reusing a stale saved city.
   - Clears committed destination and prior discovery/exhaustion context before a new Near Me scan.
   - Re-centers the map on the fresh device fix before launching Recommendation Intelligence.
   - Correctly unwraps destination event payloads before restoring the search field.
   - Restores the committed destination label during initialization and panel reopen.
   - Gives destination-field focus exclusive authority over autocomplete and saved results.
   - Keeps saved results retained but hidden while the user edits the destination.
   - Clears destination and linked results immediately when the user explicitly empties the field.
   - Prevents the bottom Search Another Place action from being the only way to edit a destination.
   - Deduplicates repeated canonical city labels before writing the search field.
   - Makes every delayed destination-field restore idempotent.
   - Prevents provider name and formatted label from being appended twice.
   - Collapses the bottom CTA after results commit so the map remains primary.
   - Leaves destination editing available directly through the remembered search field.
   - Preserves the Places control after startup instead of hiding it with retained results.
   - Restores the original bottom CTA after results commit.
   - Labels the CTA with the committed city or district, such as SCAN TAMPA AGAIN.
   - Reruns the current destination scan instead of clearing the destination.
   - Keeps the selected category, cuisine intent, scope, and canonical destination intact.
   - Requests a fresh market pass without destroying the existing result session first.
   - Removes the unintended floating Scan Again control introduced by UI V600.53.
   - Makes the city-specific bottom CTA reopen the Adventure menu instead of scanning immediately.
   - Preserves the committed city or district in the destination field.
   - Preserves the selected category, cuisine intent, scope, and current map pins.
   - Keeps existing results alive until the user explicitly starts the next scan.
   - Lets the user change category, cuisine, destination, or scope before scanning again.

   Load after:
   1. adventure-config.js
   2. adventure-utils.js
   3. adventure-state.js
   4. adventure-events.js
   5. adventure-destination.js
   6. adventure-search.js
   7. adventure-scan.js
   8. adventure-provider.js
   9. adventure-map.js
   10. adventure-ui.js
*/

(function (window, document) {
  "use strict";

  if (window.DriveIQAdventureControls) {
    return;
  }

  var config = window.DriveIQAdventureConfig;
  var utils = window.DriveIQAdventureUtils;
  var state = window.DriveIQAdventureState;
  var events = window.DriveIQAdventureEvents;
  var destinationController = window.DriveIQAdventureDestination;
  var searchController = window.DriveIQAdventureSearch;
  var scanController = window.DriveIQAdventureScan;
  var provider = window.DriveIQAdventureProvider;
  var mapController = window.DriveIQAdventureMap;
  var uiController = window.DriveIQAdventureUI;

  if (
    !config ||
    !utils ||
    !state ||
    !events ||
    !destinationController ||
    !searchController ||
    !scanController ||
    !provider ||
    !mapController ||
    !uiController
  ) {
    throw new Error(
      "adventure-controls.js requires config, utils, state, events, destination, search, scan, provider, map, and UI."
    );
  }

  var initialized = false;
  var activeCategory = config.scan.defaultCategory;
  var activeQuery = "";
  var activeScope = config.scan.defaultScope;
  var gpsRequestToken = 0;
  var searchChoiceToken = 0;
  var lastSearchChoiceKey = "";
  var lastSearchChoiceAt = 0;
  var searchAgainReady = false;
  var activeNaturalIntent = null;
  var lastIntentKey = "";
  var lastIntentAt = 0;
  var intentExecutionToken = 0;
  var boundElements = [];
  var suppressedTouchSurfaces = [];
  var touchRecoveryTimers = [];
  var lastTouchAuthorityReason = "";
  var lastTouchAuthorityAt = 0;
  var lastPresentationToken = "";
  var lastPresentationGeneration = 0;
  var lastPresentationCount = 0;
  var presentationSettlementTimers = [];

  var ids = {
    search: "adventureSearch",
    clearSearch: "clearAdventureSearch",
    explore: "buildAdventure",
    scanNearMe: "lockGps",
    centerMap: "centerMap",
    chips: "adventureChips",
    scopeButtons: ".adventureCenterSwitch",
    centerValue: "adventureCenterValue",
    contextLine: "adventureContextLine",
    startAdventure: "startAdventure",
    scanPeek: "adventureScanPeek",
    scanPeekButton: "adventureScanPeekBtn",
    minimize: "adventurePanelMinimize",
    restore: "adventurePanelRestore",
    restoreSecondary: "adventureMenuRestore"
  };

  function log() {
    if (
      !config.logging.enabled ||
      !window.console ||
      !window.console.log
    ) {
      return;
    }

    var args = Array.prototype.slice.call(arguments);
    args.unshift(config.logging.prefix, "[Controls]");
    window.console.log.apply(window.console, args);
  }

  function element(id) {
    return utils.byId(id);
  }

  function normalizeCategory(value) {
    var category = utils.normText(
      value || config.scan.defaultCategory
    )
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    return category || config.scan.defaultCategory;
  }

  function normalizeScope(value) {
    var scope = utils.normText(
      value || config.scan.defaultScope
    )
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    if (scope === "destination") {
      return "near_destination";
    }

    return scope || config.scan.defaultScope;
  }

  function categoryQuery(category, fallback) {
    var queries = {
      food_iq: "must try local food restaurants bbq burgers tacos dessert coffee bakery",
      food: "food restaurants",
      local_favorites: "local favorites restaurants bars attractions",
      trending: "popular local favorites",
      live_music: "live music nightlife",
      coffee: "coffee cafe bakery local",
      hidden_gems: "hidden gems unique local places",
      waterfront: "waterfront marina beach lake river restaurants",
      outdoors: "scenic parks trails outdoors nature",
      lake_iq: "lake marina boat ramp fishing waterfront",
      sunset_iq: "sunset scenic overlook waterfront",
      family_iq: "family friendly kids attractions amusement water park aquarium zoo museum mini golf arcade playground dolphin cruise",
      dog_iq: "dog friendly parks patios trails",
      gameday: "sports bar game day nightlife",
      nightlife: "nightlife bars patios restaurants"
    };

    return String(
      fallback ||
      queries[category] ||
      category.replace(/_/g, " ")
    );
  }

  function updateActiveChip() {
    var chips = document.querySelectorAll(
      "#" + ids.chips + " .chip"
    );
    var index;
    var chipCategory;

    for (index = 0; index < chips.length; index += 1) {
      chipCategory = normalizeCategory(
        chips[index].getAttribute("data-filter")
      );

      chips[index].classList.toggle(
        "on",
        chipCategory === activeCategory
      );
    }
  }

  function updateActiveScope() {
    var buttons = document.querySelectorAll(ids.scopeButtons);
    var index;
    var scope;

    for (index = 0; index < buttons.length; index += 1) {
      scope = normalizeScope(
        buttons[index].getAttribute("data-adventure-scope")
      );

      buttons[index].classList.toggle(
        "on",
        scope === activeScope
      );
    }
  }



  function cleanIntent(intent) {
    if (!intent || typeof intent !== "object") {
      return null;
    }

    return {
      rawQuery: String(intent.rawQuery || ""),
      category: normalizeCategory(
        intent.category || activeCategory
      ),
      categoryLabel: String(intent.categoryLabel || ""),
      scope: String(intent.scope || ""),
      locationQuery: String(intent.locationQuery || ""),
      searchQuery: String(intent.searchQuery || ""),
      recommendationQuery: String(
        intent.recommendationQuery ||
        intent.searchQuery ||
        ""
      ),
      preferences: Array.isArray(intent.preferences)
        ? intent.preferences.slice()
        : [],
      usesCurrentLocation: intent.usesCurrentLocation === true,
      usesActiveRoute: intent.usesActiveRoute === true,
      shouldAutocomplete: intent.shouldAutocomplete === true,
      destination: intent.destination || null,
      source: String(
        intent.source || "natural-intent-search"
      )
    };
  }

  function intentKey(intent) {
    var supplied = cleanIntent(intent);

    if (!supplied) {
      return "";
    }

    return [
      supplied.category,
      supplied.scope,
      supplied.locationQuery,
      supplied.rawQuery,
      supplied.destination
        ? destinationKey(supplied.destination)
        : ""
    ].join("|");
  }

  function preferenceQuery(intent) {
    var supplied = cleanIntent(intent);
    var parts = [];

    if (!supplied) {
      return "";
    }

    if (supplied.preferences.indexOf("best") >= 0) {
      parts.push("best top rated");
    }

    if (supplied.preferences.indexOf("local") >= 0) {
      parts.push("local independent family owned");
    }

    if (supplied.preferences.indexOf("hidden") >= 0) {
      parts.push("hidden gems off the beaten path");
    }

    if (supplied.preferences.indexOf("open_now") >= 0) {
      parts.push("open now");
    }

    if (supplied.preferences.indexOf("family_friendly") >= 0) {
      parts.push("family friendly");
    }

    if (supplied.preferences.indexOf("date_night") >= 0) {
      parts.push("date night romantic");
    }

    if (supplied.preferences.indexOf("cheap") >= 0) {
      parts.push("affordable budget");
    }

    if (supplied.preferences.indexOf("scenic") >= 0) {
      parts.push("scenic waterfront view");
    }

    return parts.join(" ");
  }

  function applyNaturalIntent(intent) {
    var supplied = cleanIntent(intent);
    var query;

    if (!supplied) {
      return null;
    }

    activeNaturalIntent = supplied;

    setCategory(
      supplied.category,
      categoryQuery(supplied.category, "") +
        " " +
        preferenceQuery(supplied)
    );

    if (
      supplied.scope === "near_me" ||
      supplied.usesCurrentLocation
    ) {
      setScope("near_me");
    } else if (
      supplied.scope === "along_route" ||
      supplied.usesActiveRoute
    ) {
      setScope("along_route");
    } else if (
      supplied.scope === "destination"
    ) {
      setScope("near_destination");
    }

    query = String(
      supplied.recommendationQuery ||
      supplied.searchQuery ||
      supplied.rawQuery ||
      categoryQuery(supplied.category, "")
    );

    activeQuery = utils.clean(
      categoryQuery(supplied.category, query) +
      " " +
      preferenceQuery(supplied)
    );

    try {
      state.merge("search", {
        intent: supplied
      }, {
        action: "controls:natural-intent"
      });
    } catch (error) {}

    events.emit("controls:intent-applied", {
      intent: supplied,
      category: activeCategory,
      scope: activeScope,
      query: activeQuery
    });

    return supplied;
  }

  function clearNearMeDiscoveryContext(reason) {
    var resetReason = String(reason || "fresh-near-me");

    /*
     * "Near me" must never inherit a previously selected city, map market,
     * continuation cursor, or exhausted-results state.
     */
    try {
      destinationController.clear();
    } catch (destinationError) {}

    try {
      if (
        uiController &&
        typeof uiController.closePersistentShelf === "function"
      ) {
        uiController.closePersistentShelf(resetReason);
      }
    } catch (shelfError) {}

    try {
      if (
        uiController &&
        typeof uiController.hideResultsPill === "function"
      ) {
        uiController.hideResultsPill();
      }
    } catch (pillError) {}

    try {
      state.merge("results", {
        items: [],
        count: 0,
        exhausted: false,
        continuationToken: null,
        marketKey: "",
        center: null
      }, {
        action: "controls:near-me-results-reset"
      });
    } catch (resultsError) {}

    try {
      state.merge("scan", {
        scope: "near_me",
        exhausted: false,
        continuationToken: null,
        marketKey: "",
        previousResultIds: [],
        seenResultIds: []
      }, {
        action: "controls:near-me-scan-reset"
      });
    } catch (scanStateError) {}

    try {
      events.emit("results:reset-requested", {
        reason: resetReason,
        scope: "near_me"
      });
      events.emit("scan:context-reset", {
        reason: resetReason,
        scope: "near_me"
      });
    } catch (eventError) {}

    return true;
  }

  function routeCenter() {
    return (
      validCoordinate(
        state.get("route.currentPosition", null)
      ) ||
      validCoordinate(
        state.get("location.lastFix", null)
      ) ||
      validCoordinate(
        destinationController.current()
      ) ||
      validCoordinate(
        state.get("map.camera.center", null)
      )
    );
  }

  function executeNaturalIntent(intent) {
    var supplied = applyNaturalIntent(intent);
    var key;
    var timestamp = Date.now();
    var token;

    if (!supplied) {
      return false;
    }

    key = intentKey(supplied);

    if (
      key &&
      key === lastIntentKey &&
      timestamp - lastIntentAt < 1200
    ) {
      return true;
    }

    lastIntentKey = key;
    lastIntentAt = timestamp;
    intentExecutionToken += 1;
    token = intentExecutionToken;

    if (
      supplied.scope === "destination" &&
      supplied.destination
    ) {
      startScan({
        center: supplied.destination,
        scope: "near_destination",
        category: supplied.category,
        query: activeQuery
      });
      return true;
    }

    if (
      supplied.scope === "destination" &&
      !supplied.destination
    ) {
      /*
       * Search IQ still owns destination autocomplete. Controls waits for the
       * selected destination and then resumes this same intent.
       */
      return false;
    }

    if (
      supplied.scope === "along_route" ||
      supplied.usesActiveRoute
    ) {
      var center = routeCenter();

      if (!center) {
        uiController.showToast(
          "Start a route before searching along it."
        );
        return false;
      }

      startScan({
        center: center,
        scope: "along_route",
        category: supplied.category,
        query: activeQuery
      });

      return true;
    }

    if (
      supplied.scope === "near_me" ||
      supplied.usesCurrentLocation
    ) {
      /*
       * Never trust restored location, destination, camera center, or an old
       * market pool for an explicit "near me" request. A saved Nashville fix
       * can be perfectly valid coordinates while still being geographically
       * wrong for the user's current session.
       */
      clearNearMeDiscoveryContext("natural-intent-near-me");
      setScope("near_me");

      requestLocation(function (fix) {
        if (
          token !== intentExecutionToken ||
          !fix
        ) {
          return;
        }

        mapController.setCenter(fix, {
          zoom: config.map.resultZoom,
          duration: 450
        });

        startScan({
          center: fix,
          scope: "near_me",
          category: supplied.category,
          query: activeQuery,
          requireExactCenter: true,
          resetDiscovery: true,
          forceFreshMarket: true,
          intent: supplied
        });
      }, {
        maximumAge: 0,
        timeout: 12000
      });

      return true;
    }

    return false;
  }

  function destinationKey(item) {
    if (!item) {
      return "";
    }

    return String(
      item.placeId ||
      item.place_id ||
      item.id ||
      item.name ||
      item.label ||
      ""
    ) + "|" +
    String(
      item.lat != null
        ? item.lat
        : item.latitude != null
          ? item.latitude
          : ""
    ) + "|" +
    String(
      item.lng != null
        ? item.lng
        : item.longitude != null
          ? item.longitude
          : ""
    );
  }

  function selectedDestinationLabel(item) {
    return String(
      item && (
        item.name ||
        item.label
      ) || ""
    );
  }

  function destinationFromEvent(event) {
    var payload = event && event.payload
      ? event.payload
      : event || {};

    return (
      payload.destination ||
      payload.item ||
      payload.result ||
      payload.place ||
      (
        payload.payload &&
        (
          payload.payload.destination ||
          payload.payload.item ||
          payload.payload.result ||
          payload.payload.place
        )
      ) ||
      (
        payload &&
        (
          payload.name ||
          payload.label ||
          payload.placeId ||
          payload.id
        )
          ? payload
          : null
      )
    );
  }

  function normalizedLabelPart(value) {
    return String(value || "")
      .replace(/\s+/g, " ")
      .replace(/\s*,\s*/g, ", ")
      .replace(/^[,\s]+|[,\s]+$/g, "")
      .trim();
  }

  function deduplicateDestinationLabel(value) {
    var label = normalizedLabelPart(value);
    var parts;
    var half;
    var left;
    var right;
    var seen = {};
    var unique = [];

    if (!label) {
      return "";
    }

    /*
      Exact repeated-half guard:
      "Destin, Florida, Destin, Florida" -> "Destin, Florida"
    */
    parts = label.split(",").map(function (part) {
      return normalizedLabelPart(part);
    }).filter(Boolean);

    if (parts.length >= 2 && parts.length % 2 === 0) {
      half = parts.length / 2;
      left = parts.slice(0, half).join(", ").toLowerCase();
      right = parts.slice(half).join(", ").toLowerCase();

      if (left === right) {
        return parts.slice(0, half).join(", ");
      }
    }

    /*
      Defensive repeated-token guard for provider payloads that append the
      same city/state sequence more than once.
    */
    parts.forEach(function (part) {
      var key = part.toLowerCase();

      if (!seen[key]) {
        seen[key] = true;
        unique.push(part);
      }
    });

    return unique.join(", ");
  }

  function destinationLabelCandidates(item) {
    var destination = item || destinationController.current() || {};
    var values = [];

    [
      destination.canonicalLabel,
      destination.displayName,
      destination.formattedAddress,
      destination.full,
      destination.label,
      destination.name
    ].forEach(function (value) {
      var normalized = deduplicateDestinationLabel(value);

      if (normalized) {
        values.push(normalized);
      }
    });

    return values;
  }

  function canonicalDestinationLabel(item) {
    var destination = item || destinationController.current();
    var label = "";
    var candidates = destinationLabelCandidates(destination);

    try {
      if (
        destinationController &&
        typeof destinationController.canonicalDisplayValue === "function"
      ) {
        label = deduplicateDestinationLabel(
          destinationController.canonicalDisplayValue(destination)
        );
      }
    } catch (error) {
      label = "";
    }

    if (label) {
      return label;
    }

    return candidates.length
      ? candidates[0]
      : "";
  }

  function hideSavedResultsForEditing(reason) {
    try {
      if (
        uiController &&
        typeof uiController.beginDestinationEditing === "function"
      ) {
        uiController.beginDestinationEditing(
          String(reason || "controls-destination-edit")
        );
      }
    } catch (error) {}

    try {
      if (
        scanController &&
        typeof scanController.suspendShelfForDestinationEditing === "function"
      ) {
        scanController.suspendShelfForDestinationEditing(
          String(reason || "controls-destination-edit")
        );
      } else if (
        scanController &&
        typeof scanController.minimizeResultShelf === "function"
      ) {
        scanController.minimizeResultShelf(
          String(reason || "controls-destination-edit")
        );
      }
    } catch (error) {}

    try {
      if (
        uiController &&
        typeof uiController.hideResultsPill === "function"
      ) {
        uiController.hideResultsPill();
      }
    } catch (error) {}

    return true;
  }

  function restoreCommittedSearchValue(reason) {
    var destination = destinationController.current();
    var input = element(ids.search);
    var label = canonicalDestinationLabel(destination);

    if (!input || !destination || !label) {
      return false;
    }

    if (
      normalizedLabelPart(input.value).toLowerCase() !==
      normalizedLabelPart(label).toLowerCase()
    ) {
      input.value = label;
    }

    input.setAttribute(
      "data-diq-selected-destination",
      destinationKey(destination)
    );
    input.setAttribute(
      "data-diq-committed-label",
      label
    );

    try {
      if (
        destinationController &&
        typeof destinationController.syncDisplay === "function"
      ) {
        destinationController.syncDisplay(
          String(reason || "controls-restore")
        );
      }
    } catch (error) {}

    return true;
  }

  function preserveSearchSelection(item) {
    var input = element(ids.search);
    var label = canonicalDestinationLabel(item);

    if (!input || !label) {
      return false;
    }

    if (
      normalizedLabelPart(input.value).toLowerCase() !==
      normalizedLabelPart(label).toLowerCase()
    ) {
      input.value = label;
    }

    input.setAttribute(
      "data-diq-selected-destination",
      destinationKey(item)
    );
    input.setAttribute(
      "data-diq-committed-label",
      label
    );

    [0, 80, 220].forEach(function (delay) {
      window.setTimeout(function () {
        var current = destinationController.current() || item;
        var currentLabel = canonicalDestinationLabel(current);

        if (
          input &&
          currentLabel &&
          normalizedLabelPart(input.value).toLowerCase() !==
            normalizedLabelPart(currentLabel).toLowerCase()
        ) {
          input.value = currentLabel;
        }
      }, delay);
    });

    return true;
  }


  function suppressFloatingScanAgainControl() {
    var control = document.getElementById("diqV600ScanAgain");

    try {
      if (
        uiController &&
        typeof uiController.hideScanAgainControl === "function"
      ) {
        uiController.hideScanAgainControl();
      }
    } catch (error) {}

    if (control) {
      control.className = "";
      control.style.setProperty("display", "none", "important");
      control.style.setProperty("visibility", "hidden", "important");
      control.style.setProperty("opacity", "0", "important");
      control.style.setProperty("pointer-events", "none", "important");
      control.setAttribute("aria-hidden", "true");
      control.setAttribute("data-diq-retired-control", "1");
    }

    return true;
  }

  function conciseDestinationName(destination) {
    var label = canonicalDestinationLabel(
      destination || destinationController.current()
    );
    var firstPart;

    if (!label) {
      return "";
    }

    firstPart = normalizedLabelPart(
      label.split(",")[0]
    );

    return firstPart || label;
  }

  function scanAgainButtonLabel() {
    var destination = destinationController.current();
    var name = conciseDestinationName(destination);

    if (
      activeScope === "near_destination" &&
      name
    ) {
      return "SCAN " + name.toUpperCase() + " AGAIN";
    }

    if (activeScope === "along_route") {
      return "SCAN ROUTE AGAIN";
    }

    return "SCAN NEAR ME AGAIN";
  }

  function primaryActionButton() {
    return element(ids.startAdventure);
  }

  function hidePrimaryActionAfterResults() {
    var button = primaryActionButton();

    if (!button) {
      return false;
    }

    searchAgainReady = true;
    button.disabled = false;
    button.classList.remove("locked");
    button.style.setProperty("display", "none", "important");
    button.setAttribute("aria-hidden", "true");
    button.setAttribute("data-diq-action", "results-retained");

    if (button.parentElement) {
      button.parentElement.style.setProperty("display", "none", "important");
    }

    return true;
  }

  function clearPresentationSettlementTimers() {
    presentationSettlementTimers.forEach(function (timer) {
      try {
        window.clearTimeout(timer);
      } catch (error) {}
    });

    presentationSettlementTimers = [];
    return true;
  }

  function ensureCompactShelfAvailable(reason) {
    var shelf = document.getElementById("diqV600ResultShelf");
    var count = Number(state.get("results.count", 0) || 0);

    if (
      count < 1 ||
      document.body.classList.contains(
        "diq-v600-destination-editing"
      ) ||
      state.get("destination.editing", false) === true
    ) {
      return false;
    }

    if (
      shelf &&
      shelf.classList.contains("on")
    ) {
      forceInteractive(shelf);
      return true;
    }

    try {
      if (
        scanController &&
        typeof scanController.openResultShelf === "function"
      ) {
        return scanController.openResultShelf(
          String(reason || "controls-presentation-fallback")
        ) !== false;
      }
    } catch (error) {}

    return false;
  }

  function settlePresentationControls(payload, source) {
    var supplied = payload && payload.payload
      ? payload.payload
      : payload || {};
    var token = String(supplied.token || "");
    var generation = Number(supplied.generation || 0);
    var count = Number(
      supplied.count ||
      state.get("results.count", 0) ||
      0
    );
    var sourceLabel = String(
      source || "scan-presentation-ready"
    );

    if (count < 1) {
      return false;
    }

    if (
      token &&
      token === lastPresentationToken &&
      generation &&
      generation === lastPresentationGeneration
    ) {
      return true;
    }

    lastPresentationToken = token;
    lastPresentationGeneration = generation;
    lastPresentationCount = count;

    clearPresentationSettlementTimers();
    setPanelMinimized(true);
    hidePrimaryActionAfterResults();

    /*
      Scan IQ V600.78 opens the compact shelf before emitting presentation-ready.
      Controls only verifies that settlement and applies a guarded fallback.
    */
    [0, 120, 320].forEach(function (delay) {
      presentationSettlementTimers.push(
        window.setTimeout(function () {
          var pill = document.getElementById(
            "diqV600ResultsPill"
          );
          var shelf = document.getElementById(
            "diqV600ResultShelf"
          );

          scheduleTouchAuthorityRecovery(
            sourceLabel + "-" + String(delay)
          );

          if (
            delay >= 120 &&
            supplied.shelfAutoOpened !== true &&
            !(
              shelf &&
              shelf.classList.contains("on")
            )
          ) {
            ensureCompactShelfAvailable(
              sourceLabel + "-shelf-fallback"
            );
          }

          forceInteractive(pill);

          if (
            shelf &&
            shelf.classList.contains("on")
          ) {
            forceInteractive(shelf);
          }

          try {
            events.emit(
              "controls:presentation-settled",
              {
                token: token,
                generation: generation,
                count: count,
                delay: delay,
                shelfVisible: !!(
                  shelf &&
                  shelf.classList.contains("on")
                ),
                pillVisible: !!(
                  pill &&
                  pill.classList.contains("on")
                ),
                source: sourceLabel
              }
            );
          } catch (eventError) {}
        }, delay)
      );
    });

    return true;
  }

  function recoverPostScanDiscovery(source, payload) {
    var count = Number(
      payload && payload.count ||
      state.get("results.count", 0) ||
      0
    );
    var items = state.get("results.items", []) || [];
    var scanStatus = null;

    suppressFloatingScanAgainControl();

    if (count < 1 && items && items.length) {
      count = items.length;
    }

    try {
      scanStatus =
        scanController &&
        typeof scanController.status === "function"
          ? scanController.status()
          : null;
    } catch (statusError) {
      scanStatus = null;
    }

    if (
      count < 1 ||
      (
        scanStatus &&
        scanStatus.presentationReady === false
      )
    ) {
      setPanelMinimized(false);
      setPrimaryActionState("idle");
      return false;
    }

    settlePresentationControls(
      payload || {},
      String(source || "controls-post-scan")
    );

    try {
      events.emit("controls:post-scan-recovered", {
        source: String(source || "controls-post-scan"),
        count: count,
        presentationReady: true,
        shelfAutoOpened: !!(
          payload &&
          payload.shelfAutoOpened
        ),
        touchAuthorityReleased: true
      });
    } catch (eventError) {}

    return true;
  }

  function setPrimaryActionState(mode) {
    var button = primaryActionButton();
    var normalized = String(mode || "idle");

    if (!button) {
      return false;
    }

    if (normalized === "search_again") {
      /*
        Remembered-Market Menu CTA Authority:
        preserve the city-specific bottom action, but reopen the full controls
        before any replacement scan begins.
      */
      searchAgainReady = true;
      suppressFloatingScanAgainControl();

      button.style.display = "";
      button.removeAttribute("aria-hidden");
      button.disabled = false;
      button.classList.remove("locked");
      button.textContent = scanAgainButtonLabel();
      button.setAttribute(
        "aria-label",
        scanAgainButtonLabel()
      );
      button.setAttribute(
        "data-diq-action",
        "open-current-market-menu"
      );
      button.setAttribute(
        "title",
        "Review this market and choose what to scan next"
      );

      if (button.parentElement) {
        button.parentElement.style.display = "";
      }

      return true;
    }

    if (normalized === "scanning") {
      searchAgainReady = false;
      button.style.display = "";
      button.removeAttribute("aria-hidden");

      if (button.parentElement) {
        button.parentElement.style.display = "";
      }

      button.disabled = true;
      button.classList.add("locked");
      button.textContent = "SCANNING...";
      button.setAttribute(
        "aria-label",
        "Adventure scan in progress"
      );
      return true;
    }

    searchAgainReady = false;
    button.style.display = "";
    button.removeAttribute("aria-hidden");

    if (button.parentElement) {
      button.parentElement.style.display = "";
    }

    button.disabled = true;
    button.classList.add("locked");
    button.textContent = "CHOOSE A SEARCH OR TAP EXPLORE";
    button.setAttribute(
      "aria-label",
      "Choose a search or tap Explore"
    );

    return true;
  }

  function closePlaceForSearchAgain() {
    try {
      if (
        window.DriveIQAdventurePlaceCard &&
        typeof window.DriveIQAdventurePlaceCard.close === "function"
      ) {
        window.DriveIQAdventurePlaceCard.close();
        return true;
      }
    } catch (error) {}

    try {
      events.emit("place:close-requested", {
        reason: "search-another-place"
      });
    } catch (eventError) {}

    return false;
  }

  function focusSearchField() {
    var input = element(ids.search);

    if (!input) {
      return false;
    }

    window.setTimeout(function () {
      try {
        input.focus();
        input.setSelectionRange(
          input.value.length,
          input.value.length
        );
      } catch (error) {}
    }, 120);

    return true;
  }

  function handleSearchAgain() {
    var destination = destinationController.current();
    var source = "bottom-primary-remembered-menu";

    suppressFloatingScanAgainControl();
    closePlaceForSearchAgain();

    /*
      Remembered-Market Menu Authority:
      this action does not scan, clear, or replace anything. It simply restores
      the Adventure controls around the current committed market so the user
      can refine the next decision before launching another scan.
    */
    try {
      if (
        scanController &&
        typeof scanController.minimizeResultShelf === "function"
      ) {
        scanController.minimizeResultShelf(source);
      }
    } catch (shelfError) {}

    try {
      if (
        uiController &&
        typeof uiController.hideResultsPill === "function"
      ) {
        uiController.hideResultsPill();
      }
    } catch (pillError) {}

    restoreCommittedSearchValue(source);
    updateActiveChip();
    updateActiveScope();
    updateContextUI();
    setPanelMinimized(false);

    try {
      uiController.showMenu({
        force: true
      });
    } catch (menuError) {
      try {
        uiController.showMenu();
      } catch (fallbackError) {}
    }

    /*
      Reassert the remembered destination after UI callbacks settle. Do not
      focus the field automatically—the user may want to change only cuisine,
      category, or scope.
    */
    [0, 80, 220].forEach(function (delay) {
      window.setTimeout(function () {
        restoreCommittedSearchValue(
          source + "-" + String(delay)
        );
        suppressFloatingScanAgainControl();
      }, delay);
    });

    setPrimaryActionState("search_again");

    try {
      events.emit("controls:scan-again-menu-opened", {
        source: source,
        destination: destination || null,
        destinationLabel: canonicalDestinationLabel(destination),
        category: activeCategory,
        scope: activeScope,
        query: activeQuery,
        retainedResultCount: Number(
          state.get("results.count", 0) || 0
        )
      });
    } catch (eventError) {}

    return true;
  }


  function handlePrimaryAction() {
    if (
      searchAgainReady ||
      state.get("results.count", 0) > 0
    ) {
      return handleSearchAgain();
    }

    return handleExplore();
  }

  function updateContextUI() {
    var centerValue = element(ids.centerValue);
    var contextLine = element(ids.contextLine);
    var destination = destinationController.current();
    var lastFix = state.get("location.lastFix", null);

    if (centerValue) {
      if (activeScope === "near_destination" && destination) {
        centerValue.textContent =
          destination.name ||
          destination.label ||
          "Destination";
      } else if (activeScope === "along_route") {
        centerValue.textContent = "Along your route";
      } else if (lastFix) {
        centerValue.textContent = "Around your location";
      } else {
        centerValue.textContent = "What’s worth exploring?";
      }
    }

    if (contextLine) {
      if (activeScope === "near_destination" && destination) {
        contextLine.textContent =
          "📍 Exploring around " +
          String(destination.name || destination.label || "destination");
      } else if (activeScope === "along_route") {
        contextLine.textContent =
          "🛣 Exploring stops along your active route.";
      } else {
        contextLine.textContent =
          "📍 Leave blank to explore around you.";
      }
    }
  }

  function setCategory(category, query) {
    activeCategory = normalizeCategory(category);
    activeQuery = String(
      query || categoryQuery(activeCategory, "")
    );

    state.merge("scan", {
      category: activeCategory
    }, {
      action: "controls:category"
    });

    updateActiveChip();

    events.emit("controls:category-changed", {
      category: activeCategory,
      query: activeQuery
    });

    return activeCategory;
  }

  function setScope(scope) {
    activeScope = normalizeScope(scope);

    state.merge("scan", {
      scope: activeScope
    }, {
      action: "controls:scope"
    });

    updateActiveScope();
    updateContextUI();

    events.emit("controls:scope-changed", {
      scope: activeScope
    });

    return activeScope;
  }

  function validCoordinate(value) {
    var lat;
    var lng;

    if (!value) {
      return null;
    }

    lat = Number(
      typeof value.lat !== "undefined"
        ? value.lat
        : value.latitude
    );

    lng = Number(
      typeof value.lng !== "undefined"
        ? value.lng
        : value.longitude
    );

    if (
      !isFinite(lat) ||
      !isFinite(lng) ||
      Math.abs(lat) > 90 ||
      Math.abs(lng) > 180
    ) {
      return null;
    }

    return {
      lat: lat,
      lng: lng
    };
  }

  function currentScanCenter() {
    var destination = destinationController.current();
    var lastFix = state.get("location.lastFix", null);
    var camera = state.get("map.camera.center", null);

    if (activeScope === "near_destination") {
      return (
        validCoordinate(destination) ||
        validCoordinate(lastFix) ||
        validCoordinate(camera)
      );
    }

    if (activeScope === "along_route") {
      return (
        validCoordinate(lastFix) ||
        validCoordinate(destination) ||
        validCoordinate(camera)
      );
    }

    return (
      validCoordinate(lastFix) ||
      validCoordinate(camera) ||
      validCoordinate(destination)
    );
  }

  function startScan(options) {
    var supplied = options || {};
    var center = validCoordinate(supplied.center);

    if (!center && !supplied.requireExactCenter) {
      center = currentScanCenter();
    }
    var query = String(
      supplied.query ||
      activeQuery ||
      categoryQuery(activeCategory, "")
    );

    if (!center) {
      uiController.showToast(
        "Location is not ready yet."
      );
      return false;
    }

    provider.register();

    return scanController.start({
      category: supplied.category || activeCategory,
      scope: supplied.scope || activeScope,
      center: center,
      query: query,
      radiusMeters: supplied.radiusMeters,
      resetDiscovery: supplied.resetDiscovery === true,
      forceFreshMarket: supplied.forceFreshMarket === true,
      intent: supplied.intent || activeNaturalIntent || null
    });
  }

  function requestLocation(callback, options) {
    var supplied = options || {};
    var token;

    if (!navigator.geolocation) {
      uiController.showToast(
        "Location is unavailable on this device."
      );
      callback(null);
      return;
    }

    gpsRequestToken += 1;
    token = gpsRequestToken;

    navigator.geolocation.getCurrentPosition(
      function (position) {
        var fix;

        if (token !== gpsRequestToken) {
          return;
        }

        fix = {
          lat: Number(position.coords.latitude),
          lng: Number(position.coords.longitude),
          accuracyMeters: Number(
            position.coords.accuracy || 0
          ),
          timestamp: Date.now()
        };

        state.merge("location", {
          lastFix: fix,
          permission: "granted",
          accuracyMeters: fix.accuracyMeters,
          updatedAt: fix.timestamp
        }, {
          action: "controls:gps-fix"
        });

        try {
          utils.lsSet(
            config.storage.lastFix,
            JSON.stringify(fix)
          );
        } catch (error) {}

        updateContextUI();
        callback(fix);
      },
      function (error) {
        if (token !== gpsRequestToken) {
          return;
        }

        state.merge("location", {
          permission: "denied"
        }, {
          action: "controls:gps-error"
        });

        if (!supplied.silent) {
          uiController.showToast(
            error && error.code === 1
              ? "Location permission is required."
              : "Could not get your location."
          );
        }

        callback(null);
      },
      {
        enableHighAccuracy: true,
        timeout: Number(supplied.timeout || 9000),
        maximumAge: Number(supplied.maximumAge || 15000)
      }
    );
  }

  function handleExplore() {
    var searchInput = element(ids.search);
    var query = utils.clean(
      searchInput ? searchInput.value : ""
    );
    var destination = destinationController.current();
    var intent = null;

    try {
      if (
        searchController &&
        typeof searchController.parseIntent === "function"
      ) {
        intent = searchController.parseIntent(query);
      }
    } catch (intentError) {
      intent = null;
    }

    if (intent) {
      activeNaturalIntent = cleanIntent(intent);

      /*
       * Near Me and Along Route intents should execute immediately. Explicit
       * destination intent continues through canonical autocomplete.
       */
      if (
        intent.scope === "near_me" ||
        intent.scope === "along_route"
      ) {
        searchController.close();
        return executeNaturalIntent(intent);
      }

      if (
        intent.scope === "destination" &&
        destination
      ) {
        intent.destination = destination;
        return executeNaturalIntent(intent);
      }

      if (
        intent.scope === "destination" &&
        intent.locationQuery
      ) {
        searchController.search(query, {
          source: "explore-natural-intent"
        });

        uiController.showToast(
          "Choose the destination for this recommendation."
        );

        return false;
      }
    }

    if (query && !destination) {
      searchController.search(query, {
        source: "explore-button"
      });

      uiController.showToast(
        "Choose a destination match."
      );

      return false;
    }

    if (destination && activeScope === "near_me") {
      setScope("near_destination");
    }

    if (currentScanCenter()) {
      startScan({
        query: activeQuery || query
      });
      return true;
    }

    requestLocation(function (fix) {
      if (fix) {
        setScope("near_me");
        startScan({
          center: fix,
          query: activeQuery || query
        });
      }
    });

    return true;
  }

  function handleScanNearMe() {
    setScope("near_me");

    requestLocation(function (fix) {
      if (!fix) {
        return;
      }

      mapController.setCenter(fix, {
        zoom: config.map.resultZoom,
        duration: 450
      });

      startScan({
        center: fix,
        scope: "near_me"
      });
    });

    return true;
  }

  function handleCenterMap() {
    var center = currentScanCenter();

    if (center) {
      mapController.setCenter(center, {
        zoom: config.map.resultZoom,
        duration: 450
      });
      return true;
    }

    requestLocation(function (fix) {
      if (fix) {
        mapController.setCenter(fix, {
          zoom: config.map.resultZoom,
          duration: 450
        });
      }
    });

    return true;
  }

  function handleClearSearch() {
    var input = element(ids.search);

    searchChoiceToken += 1;
    lastSearchChoiceKey = "";
    lastSearchChoiceAt = 0;
    activeNaturalIntent = null;
    lastIntentKey = "";
    lastIntentAt = 0;
    intentExecutionToken += 1;

    if (input) {
      input.value = "";
    }

    searchController.clear();
    destinationController.clear();
    setScope("near_me");
    uiController.showMenu();
    setPrimaryActionState("idle");

    return true;
  }

  function handleSearchFocus(event) {
    var input = event.currentTarget;
    var committed = destinationController.current();
    var committedLabel = canonicalDestinationLabel(committed);

    hideSavedResultsForEditing("search-field-focus");

    if (state.get("results.count", 0) > 0) {
      setPrimaryActionState("search_again");
    }

    if (
      input &&
      committed &&
      committedLabel &&
      !utils.clean(input.value)
    ) {
      input.value = committedLabel;
    }

    try {
      if (
        destinationController &&
        typeof destinationController.beginEdit === "function"
      ) {
        destinationController.beginEdit(
          input ? input.value : committedLabel,
          "controls-search-focus"
        );
      }
    } catch (error) {}

    return true;
  }

  function handleSearchInput(event) {
    var input = event.currentTarget;
    var rawValue = String(input.value || "");
    var query = utils.clean(rawValue);
    var committed = destinationController.current();
    var committedLabel = canonicalDestinationLabel(committed);

    hideSavedResultsForEditing("search-field-input");

    try {
      if (
        destinationController &&
        typeof destinationController.updateEditQuery === "function"
      ) {
        destinationController.updateEditQuery(
          rawValue,
          "controls-search-input"
        );
      }
    } catch (error) {}

    /*
      Explicitly emptying the field is the user's clear action.
      Destination V600.46 then atomically clears the linked results and pins.
    */
    if (!query) {
      activeNaturalIntent = null;
      searchController.clear();
      destinationController.clear(
        "user-cleared-search-field"
      );
      setScope("near_me");
      setPrimaryActionState("idle");
      return;
    }

    /*
      Tapping a remembered destination should still allow autocomplete to
      reopen. Do not return just because the text equals the committed label.
    */
    if (
      committed &&
      committedLabel &&
      query === committedLabel
    ) {
      searchController.debounce(query, {
        source: "search-input-committed-label"
      });
      return;
    }

    try {
      activeNaturalIntent =
        typeof searchController.parseIntent === "function"
          ? cleanIntent(
              searchController.parseIntent(query)
            )
          : null;
    } catch (intentError) {
      activeNaturalIntent = null;
    }

    searchController.debounce(query, {
      source: "search-input"
    });
  }

  function handleSearchKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      handleClearSearch();
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      handleExplore();
    }
  }

  function handleChipClick(event) {
    var chip = event.currentTarget;
    var center;
    var destination;

    setCategory(
      chip.getAttribute("data-filter"),
      chip.getAttribute("data-query")
    );

    events.emit("controls:chip-selected", {
      category: activeCategory,
      query: activeQuery
    });

    /*
      V600.12 Category Rescan Authority:
      A category tap is a discovery action, not only a visual selection.
      Reuse the current committed scope/center and launch a fresh scan through
      the existing Scan IQ controller. Do not clear the active result session
      until Scan IQ replaces it with the new immutable result set.
    */
    destination = destinationController.current();

    if (activeScope === "near_destination" && destination) {
      startScan({
        center: destination,
        scope: "near_destination",
        category: activeCategory,
        query: activeQuery,
        forceFreshMarket: true
      });
      return;
    }

    if (activeScope === "along_route") {
      center = routeCenter();

      if (!center) {
        uiController.showToast(
          "Start a route before searching along it."
        );
        return;
      }

      startScan({
        center: center,
        scope: "along_route",
        category: activeCategory,
        query: activeQuery,
        forceFreshMarket: true
      });
      return;
    }

    /*
      Near Me should prefer the freshest known device fix. If one is not
      available, request a fresh GPS fix and then run the category scan.
    */
    center = validCoordinate(
      state.get("location.lastFix", null)
    );

    if (center) {
      startScan({
        center: center,
        scope: "near_me",
        category: activeCategory,
        query: activeQuery,
        forceFreshMarket: true
      });
      return;
    }

    requestLocation(function (fix) {
      if (!fix) {
        return;
      }

      startScan({
        center: fix,
        scope: "near_me",
        category: activeCategory,
        query: activeQuery,
        forceFreshMarket: true
      });
    }, {
      maximumAge: 0,
      timeout: 12000
    });
  }

  function handleScopeClick(event) {
    setScope(
      event.currentTarget.getAttribute(
        "data-adventure-scope"
      )
    );
  }

  function clearTouchRecoveryTimers() {
    touchRecoveryTimers.forEach(function (timer) {
      try {
        window.clearTimeout(timer);
      } catch (error) {}
    });
    touchRecoveryTimers = [];
  }

  function protectedTouchSurface(node) {
    if (!node || node.nodeType !== 1) {
      return true;
    }

    return !!(
      node.id === "diqV600ResultsPill" ||
      node.id === "diqV600ResultShelf" ||
      node.id === ids.restore ||
      node.id === ids.restoreSecondary ||
      node.id === ids.scanPeek ||
      node.id === ids.scanPeekButton ||
      (
        node.closest &&
        (
          node.closest("#diqV600ResultsPill") ||
          node.closest("#diqV600ResultShelf") ||
          node.closest(".mapboxgl-map") ||
          node.closest(".mapboxgl-marker") ||
          node.closest("[data-diq-map-pin]") ||
          node.closest("[data-diq-place-card]")
        )
      ) ||
      (
        node.querySelector &&
        node.querySelector(".mapboxgl-canvas")
      )
    );
  }

  function coversMostViewport(node) {
    var rect;
    var style;
    var viewportArea;
    var nodeArea;

    try {
      rect = node.getBoundingClientRect();
      style = window.getComputedStyle(node);
    } catch (error) {
      return false;
    }

    if (
      !rect ||
      rect.width < 1 ||
      rect.height < 1 ||
      style.display === "none" ||
      style.visibility === "hidden"
    ) {
      return false;
    }

    viewportArea = Math.max(
      1,
      Number(window.innerWidth || 1) *
      Number(window.innerHeight || 1)
    );
    nodeArea = Math.max(0, rect.width * rect.height);

    return !!(
      nodeArea / viewportArea >= 0.55 &&
      (
        style.position === "fixed" ||
        style.position === "absolute" ||
        node.id === "adventureApp"
      )
    );
  }

  function rememberAndDisableTouch(node, reason) {
    if (
      !node ||
      protectedTouchSurface(node) ||
      node.getAttribute("data-diq-touch-authority-disabled") === "1"
    ) {
      return false;
    }

    suppressedTouchSurfaces.push({
      node: node,
      pointerEvents: node.style.pointerEvents || "",
      ariaHidden: node.getAttribute("aria-hidden"),
      inert: node.hasAttribute("inert")
    });

    node.style.setProperty("pointer-events", "none", "important");
    node.setAttribute("data-diq-touch-authority-disabled", "1");
    node.setAttribute(
      "data-diq-touch-authority-reason",
      String(reason || "panel-minimized")
    );

    return true;
  }

  function restoreSuppressedTouchSurfaces() {
    suppressedTouchSurfaces.forEach(function (record) {
      var node = record && record.node;

      if (!node || !document.documentElement.contains(node)) {
        return;
      }

      node.style.removeProperty("pointer-events");

      if (record.pointerEvents) {
        node.style.pointerEvents = record.pointerEvents;
      }

      node.removeAttribute("data-diq-touch-authority-disabled");
      node.removeAttribute("data-diq-touch-authority-reason");

      if (record.ariaHidden === null) {
        node.removeAttribute("aria-hidden");
      } else {
        node.setAttribute("aria-hidden", record.ariaHidden);
      }

      if (!record.inert) {
        node.removeAttribute("inert");
      }
    });

    suppressedTouchSurfaces = [];
    return true;
  }

  function forceInteractive(node) {
    if (!node) {
      return false;
    }

    node.style.setProperty("pointer-events", "auto", "important");
    node.removeAttribute("inert");
    return true;
  }

  function releaseMapTouchAuthority(reason) {
    var app = element("adventureApp");
    var candidates = [];
    var selectors = [
      "#adventureMenu",
      "#adventurePanel",
      "#adventureBackdrop",
      "#adventureOverlay",
      "#adventureMenuBackdrop",
      ".adventureMenu",
      ".adventurePanel",
      ".adventureBackdrop",
      ".adventureOverlay",
      ".adventure-modal-backdrop",
      "[data-adventure-overlay]",
      "[data-diq-adventure-overlay]"
    ];
    var mapCanvas;
    var mapContainer;
    var pill;
    var shelf;
    var restore;
    var restoreSecondary;
    var scanPeekButton;

    clearTouchRecoveryTimers();

    selectors.forEach(function (selector) {
      var nodes;

      try {
        nodes = document.querySelectorAll(selector);
      } catch (error) {
        nodes = [];
      }

      Array.prototype.forEach.call(nodes, function (node) {
        if (candidates.indexOf(node) < 0) {
          candidates.push(node);
        }
      });
    });

    if (app) {
      Array.prototype.forEach.call(
        app.querySelectorAll("*"),
        function (node) {
          if (
            candidates.indexOf(node) < 0 &&
            coversMostViewport(node) &&
            !protectedTouchSurface(node)
          ) {
            candidates.push(node);
          }
        }
      );
    }

    candidates.forEach(function (node) {
      rememberAndDisableTouch(
        node,
        reason || "post-scan-touch-authority"
      );
    });

    try {
      mapCanvas = document.querySelector(".mapboxgl-canvas");
      mapContainer = document.querySelector(".mapboxgl-map");
    } catch (error) {
      mapCanvas = null;
      mapContainer = null;
    }

    pill = document.getElementById("diqV600ResultsPill");
    shelf = document.getElementById("diqV600ResultShelf");
    restore = element(ids.restore);
    restoreSecondary = element(ids.restoreSecondary);
    scanPeekButton = element(ids.scanPeekButton);

    forceInteractive(mapContainer);
    forceInteractive(mapCanvas);
    forceInteractive(pill);

    if (
      shelf &&
      shelf.classList.contains("on")
    ) {
      forceInteractive(shelf);
    }

    forceInteractive(restore);
    forceInteractive(restoreSecondary);
    forceInteractive(scanPeekButton);

    try {
      mapController.enableInteraction();
    } catch (mapError) {}

    lastTouchAuthorityReason = String(
      reason || "post-scan-touch-authority"
    );
    lastTouchAuthorityAt = Date.now();

    try {
      events.emit("controls:touch-authority-released", {
        reason: lastTouchAuthorityReason,
        suppressedCount: suppressedTouchSurfaces.length,
        mapPointerEvents: mapCanvas
          ? window.getComputedStyle(mapCanvas).pointerEvents
          : "",
        pillPointerEvents: pill
          ? window.getComputedStyle(pill).pointerEvents
          : ""
      });
    } catch (eventError) {}

    return true;
  }

  function scheduleTouchAuthorityRecovery(reason) {
    clearTouchRecoveryTimers();

    [0, 100, 300].forEach(function (delay) {
      touchRecoveryTimers.push(
        window.setTimeout(function () {
          releaseMapTouchAuthority(
            String(reason || "post-scan") +
            "-" +
            String(delay)
          );
        }, delay)
      );
    });

    return true;
  }

  function touchReceiverAtMapCenter() {
    var x = Math.round(Number(window.innerWidth || 0) / 2);
    var y = Math.round(Number(window.innerHeight || 0) / 2);
    var node = null;

    try {
      node = document.elementFromPoint(x, y);
    } catch (error) {
      node = null;
    }

    return node
      ? {
          tag: String(node.tagName || ""),
          id: String(node.id || ""),
          className: String(node.className || ""),
          pointerEvents: String(
            window.getComputedStyle(node).pointerEvents || ""
          )
        }
      : null;
  }

  function setPanelMinimized(minimized) {
    var app = element("adventureApp");

    document.body.classList.toggle(
      "adventureManualMinimized",
      minimized
    );

    if (app) {
      app.classList.toggle(
        "adventureManualMinimized",
        minimized
      );
    }

    if (minimized) {
      scheduleTouchAuthorityRecovery(
        "controls-panel-minimized"
      );
    } else {
      clearTouchRecoveryTimers();
      restoreSuppressedTouchSurfaces();

      try {
        mapController.enableInteraction();
      } catch (mapError) {}
    }

    mapController.scheduleResize(
      minimized
        ? "controls:minimize"
        : "controls:restore"
    );

    events.emit("controls:panel-toggled", {
      minimized: minimized,
      touchAuthorityReleased: minimized === true
    });

    return true;
  }

  function bindClick(id, handler) {
    var target = element(id);

    if (!target) {
      return;
    }

    target.addEventListener(
      "click",
      function (event) {
        event.preventDefault();
        handler(event);
      },
      false
    );

    boundElements.push({
      element: target,
      type: "click",
      handler: handler
    });
  }

  function bindControls() {
    var search = element(ids.search);
    var chips = document.querySelectorAll(
      "#" + ids.chips + " .chip"
    );
    var scopes = document.querySelectorAll(
      ids.scopeButtons
    );
    var index;

    bindClick(ids.explore, handleExplore);
    bindClick(ids.scanNearMe, handleScanNearMe);
    bindClick(ids.centerMap, handleCenterMap);
    bindClick(ids.clearSearch, handleClearSearch);
    bindClick(ids.startAdventure, handlePrimaryAction);

    bindClick(ids.minimize, function () {
      setPanelMinimized(true);
    });

    bindClick(ids.restore, function () {
      setPanelMinimized(false);
    });

    bindClick(ids.restoreSecondary, function () {
      setPanelMinimized(false);
    });

    bindClick(ids.scanPeekButton, function () {
      uiController.showMap();
    });

    if (search) {
      search.addEventListener(
        "focus",
        handleSearchFocus,
        false
      );

      search.addEventListener(
        "input",
        handleSearchInput,
        false
      );

      search.addEventListener(
        "keydown",
        handleSearchKeydown,
        false
      );
    }

    for (index = 0; index < chips.length; index += 1) {
      chips[index].addEventListener(
        "click",
        handleChipClick,
        false
      );
    }

    for (index = 0; index < scopes.length; index += 1) {
      scopes[index].addEventListener(
        "click",
        handleScopeClick,
        false
      );
    }
  }

  function bindEvents() {
    events.on("destination:committed", function (event) {
      var item = destinationFromEvent(event);

      if (item) {
        preserveSearchSelection(item);
      } else {
        restoreCommittedSearchValue(
          "destination-committed-fallback"
        );
      }

      setScope("near_destination");
      updateContextUI();
    });

    events.on("search:chosen", function (event) {
      var item = destinationFromEvent(event);
      var attachedIntent;
      var key;
      var token;
      var now = Date.now();

      if (!item) {
        return;
      }

      attachedIntent = cleanIntent(
        item.intent ||
        activeNaturalIntent
      );

      key = destinationKey(item);

      if (
        key &&
        key === lastSearchChoiceKey &&
        now - lastSearchChoiceAt < 1200
      ) {
        preserveSearchSelection(item);
        return;
      }

      lastSearchChoiceKey = key;
      lastSearchChoiceAt = now;
      searchChoiceToken += 1;
      token = searchChoiceToken;

      preserveSearchSelection(item);

      destinationController.commit(
        item,
        "search-choice"
      );

      setScope("near_destination");
      updateContextUI();

      mapController.setCenter(item, {
        zoom: config.map.resultZoom,
        duration: 500
      });

      window.setTimeout(function () {
        if (token !== searchChoiceToken) {
          return;
        }

        preserveSearchSelection(
          destinationController.current() || item
        );

        if (attachedIntent) {
          attachedIntent.destination =
            destinationController.current() || item;
          attachedIntent.scope = "destination";
          executeNaturalIntent(attachedIntent);
          return;
        }

        startScan({
          center: item,
          scope: "near_destination",
          category: activeCategory,
          query: activeQuery ||
            categoryQuery(activeCategory, "")
        });
      }, 220);
    });

    events.on("search:intent-chosen", function (event) {
      var intent = event && event.payload
        ? event.payload
        : null;

      if (!intent) {
        return;
      }

      activeNaturalIntent = cleanIntent(intent);

      if (
        activeNaturalIntent &&
        (
          activeNaturalIntent.scope === "near_me" ||
          activeNaturalIntent.scope === "along_route"
        )
      ) {
        executeNaturalIntent(activeNaturalIntent);
      }
    });

    events.on("scan:started", function () {
      clearPresentationSettlementTimers();
      lastPresentationToken = "";
      lastPresentationGeneration = 0;
      lastPresentationCount = 0;
      suppressFloatingScanAgainControl();
      setPrimaryActionState("scanning");
    });

    events.on("scan:completed", function () {
      restoreCommittedSearchValue("scan-completed");
    });

    events.on("scan:presentation-ready", function (event) {
      var payload = event && event.payload
        ? event.payload
        : event || {};

      recoverPostScanDiscovery(
        "scan-presentation-ready",
        payload
      );
    });

    events.on("scan:presentation-blocked", function () {
      clearPresentationSettlementTimers();
      setPanelMinimized(false);
      setPrimaryActionState("idle");
    });

    events.on("scan:failed", function () {
      clearPresentationSettlementTimers();
      setPrimaryActionState("idle");
    });

    events.on("scan:cancelled", function () {
      clearPresentationSettlementTimers();
      setPrimaryActionState("idle");
    });

    events.on("place:closed", function () {
      mapController.enableInteraction();

      if (
        document.body.classList.contains(
          "adventureManualMinimized"
        )
      ) {
        scheduleTouchAuthorityRecovery("place-closed");
      }
    });
  }

  function restoreSavedLocation() {
    var stored;

    try {
      stored = utils.lsJSON(
        config.storage.lastFix,
        null
      );
    } catch (error) {
      stored = null;
    }

    if (validCoordinate(stored)) {
      state.merge("location", {
        lastFix: stored,
        permission: "unknown",
        accuracyMeters: Number(
          stored.accuracyMeters || 0
        ),
        updatedAt: Number(
          stored.timestamp || Date.now()
        )
      }, {
        action: "controls:restore-location"
      });
    }
  }

  function init() {
    var firstChip;
    var firstScope;

    if (initialized) {
      return true;
    }

    restoreSavedLocation();

    /* V606: Coffee is the default discovery filter, not an auto-scan.
       Explicit user category selections still use the existing handlers. */
    firstChip = document.querySelector(
      "#" + ids.chips + " .chip[data-filter='coffee']"
    ) || document.querySelector(
      "#" + ids.chips + " .chip.on"
    );

    if (firstChip) {
      activeCategory = normalizeCategory(
        firstChip.getAttribute("data-filter")
      );

      activeQuery = String(
        firstChip.getAttribute("data-query") ||
        categoryQuery(activeCategory, "")
      );
    }

    firstScope = document.querySelector(
      ids.scopeButtons + ".on"
    );

    if (firstScope) {
      activeScope = normalizeScope(
        firstScope.getAttribute(
          "data-adventure-scope"
        )
      );
    }

    bindControls();
    bindEvents();
    suppressFloatingScanAgainControl();
    updateActiveChip();
    updateActiveScope();
    updateContextUI();
    restoreCommittedSearchValue("controls-init");

    if (state.get("results.count", 0) > 0) {
      try {
        if (
          scanController &&
          typeof scanController.closeResultShelf === "function"
        ) {
          scanController.closeResultShelf(
            "controls-init-menu"
          );
        }
      } catch (error) {}

      recoverPostScanDiscovery("controls-init-retained-results");
    } else {
      setPrimaryActionState("idle");
    }

    initialized = true;

    events.emit("controls:ready", {
      category: activeCategory,
      query: activeQuery,
      scope: activeScope
    });

    log("Natural Intent handoff authority initialized");

    return true;
  }

  function status() {
    return {
      build: "V600_11_PRESENTATION_CHOREOGRAPHY_AUTHORITY",
      initialized: initialized,
      activeCategory: activeCategory,
      activeQuery: activeQuery,
      activeScope: activeScope,
      activeNaturalIntent: activeNaturalIntent
        ? Object.assign({}, activeNaturalIntent)
        : null,
      searchAgainReady: searchAgainReady,
      scanCenter: currentScanCenter(),
      panelMinimized: document.body.classList.contains(
        "adventureManualMinimized"
      ),
      suppressedTouchSurfaceCount: suppressedTouchSurfaces.length,
      lastTouchAuthorityReason: lastTouchAuthorityReason,
      lastTouchAuthorityAt: lastTouchAuthorityAt,
      touchReceiverAtMapCenter: touchReceiverAtMapCenter(),
      lastPresentationToken: lastPresentationToken,
      lastPresentationGeneration: lastPresentationGeneration,
      lastPresentationCount: lastPresentationCount,
      pendingPresentationSettlementTimers:
        presentationSettlementTimers.length
    };
  }

  window.DriveIQAdventureControls = {
    init: init,
    status: status,
    setCategory: setCategory,
    setScope: setScope,
    applyNaturalIntent: applyNaturalIntent,
    executeNaturalIntent: executeNaturalIntent,
    activeNaturalIntent: function () {
      return activeNaturalIntent
        ? Object.assign({}, activeNaturalIntent)
        : null;
    },
    startScan: startScan,
    requestLocation: requestLocation,
    handleExplore: handleExplore,
    handleScanNearMe: handleScanNearMe,
    handleCenterMap: handleCenterMap,
    handleClearSearch: handleClearSearch,
    handleSearchFocus: handleSearchFocus,
    minimize: function (reason) {
      setPanelMinimized(true);
      scheduleTouchAuthorityRecovery(
        reason || "public-minimize"
      );
      return true;
    },
    restore: function () {
      setPanelMinimized(false);
      return true;
    },
    releaseMapTouchAuthority: releaseMapTouchAuthority,
    scheduleTouchAuthorityRecovery: scheduleTouchAuthorityRecovery,
    touchReceiverAtMapCenter: touchReceiverAtMapCenter,
    restoreCommittedSearchValue: restoreCommittedSearchValue,
    canonicalDestinationLabel: canonicalDestinationLabel,
    deduplicateDestinationLabel: deduplicateDestinationLabel,
    conciseDestinationName: conciseDestinationName,
    scanAgainButtonLabel: scanAgainButtonLabel,
    suppressFloatingScanAgainControl: suppressFloatingScanAgainControl,
    handleSearchAgain: handleSearchAgain,
    handlePrimaryAction: handlePrimaryAction
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      init,
      { once: true }
    );
  } else {
    init();
  }
}(window, document));
